import { ComponentType } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  extensionFunctionType,
  extensionWebhookType,
  HookType,
} from '@rossum/api-client/hooks';
import { IconCode, IconWebhook } from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Typography } from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { FUNCTIONS_LINK, WEBHOOKS_LINK } from '../../../../../constants/values';
import { LocalizationKeys } from '../../../../../i18n';
import { link } from '../../../../../lib/formaterValues';

type ConfigOption = {
  Icon: ComponentType;
  translation: LocalizationKeys;
};

// we allow to create only webhooks and functions for now
export type CreatableHookType = Extract<HookType, 'webhook' | 'function'>;

const extensionTypeConfig: Record<CreatableHookType, ConfigOption> = {
  webhook: {
    Icon: IconWebhook,
    translation:
      'containers.settings.extensions.createExtension.extensionType.webhook.label',
  },
  function: {
    Icon: IconCode,
    translation:
      'containers.settings.extensions.createExtension.extensionType.function.label',
  },
};

type Props = {
  extensionType: CreatableHookType;
  onClick: () => void;
  selected: boolean;
  dataCy?: string;
};

export const ExtensionTypeSelect = ({
  extensionType,
  onClick,
  selected,
  dataCy,
}: Props) => {
  const { Icon, translation } = extensionTypeConfig[extensionType];

  return (
    <Stack
      justifyContent="center"
      sx={{
        width: 1,
        p: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderRadius: borderRadius('m'),
        borderColor: t =>
          selected ? t.palette.primary.main : t.palette.action.hover,
        '&:hover': {
          borderColor: t =>
            selected ? t.palette.primary.main : t.palette.action.active,
        },
        backgroundColor: t =>
          selected ? t.palette.action.hover : 'transparent',
        transition: 'background-color 0.2s ease, border-color 0.2s ease',
        cursor: 'pointer',
      }}
      onClick={onClick}
      data-cy={dataCy}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <SvgIcon fontSize="large" color="action">
          <Icon />
        </SvgIcon>
        <Typography variant="body2">
          <FormattedMessage id={translation} />
        </Typography>
      </Stack>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          a: {
            color: t => t.palette.text.primary,
            textDecoration: 'underline',
          },
        }}
      >
        {extensionType === extensionFunctionType && (
          <FormattedMessage
            id="containers.settings.extensions.createExtension.extensionType.function.description"
            values={{ link: link(FUNCTIONS_LINK) }}
          />
        )}
        {extensionType === extensionWebhookType && (
          <FormattedMessage
            id="containers.settings.extensions.createExtension.extensionType.webhook.description"
            values={{ link: link(WEBHOOKS_LINK) }}
          />
        )}
      </Typography>
    </Stack>
  );
};
